import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, Card, EmptyState, Loading, ModuleHeader, SearchSelect, StatCard, Tabs, useAsync, useToast } from '../components/ui';
import { TaskListView } from '../components/TaskListView';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatRelative, SLA_LABELS } from '../lib/format';
import { IconAlert, IconBoard, IconClock, IconFlash, IconList, IconRoute, IconUsers } from '../components/icons';

/** Kanban-Board mit Drag & Drop für die Disposition (Kap. 6). */
export function DispatchPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { notify } = useToast();

  const [view, setView] = useState<'board' | 'list' | 'unassigned' | 'calendar'>('board');
  // Steuert, welcher Ausschnitt von `allTasks` in der Listenansicht erscheint –
  // eine Kennzahlen-Kachel setzt `view` und `listFilter` zusammen, ein Klick
  // auf den Reiter "Liste" selbst setzt den Filter zurück auf "alle".
  const [listFilter, setListFilter] = useState<'all' | 'critical' | 'inprogress' | 'overdue'>('all');
  const [dragged, setDragged] = useState<any>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const board = useAsync(() => api.get('/api/tasks/board', { open: 'true' }), []);
  const users = useAsync(() => api.get('/api/users'), []);
  const absences = useAsync(() => api.get('/api/catalogs/absences').catch(() => []), []);

  const move = async (task: any, status: string) => {
    if (task.status === status) return;
    // "Zugewiesen" ist kein bloßer Statuswert, den man ansteuern kann, ohne
    // jemanden zuständig zu machen – der reine Statuswechsel ließe
    // `assignee_id` unangetastet und erzeugte einen Auftrag, der "Zugewiesen"
    // heißt, aber niemandem zugeordnet ist. Die echte Zuweisung läuft über
    // den eigenen Weg (Zuweisungsliste / Zuweisen-Dialog), die dabei den
    // Status als Nebenwirkung mitsetzt.
    if (status === 'assigned') {
      notify('Bitte über die Zuweisungsliste eine Person zuweisen.', 'error');
      return;
    }
    try {
      await api.post(`/api/tasks/${task.id}/status`, { status });
      notify(`${task.task_no} → ${status}`, 'success');
      board.reload();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Statuswechsel nicht möglich.', 'error');
    }
  };

  const assign = async (taskId: number, assigneeId: number) => {
    await api.post(`/api/tasks/${taskId}/assign`, { assigneeId });
    board.reload();
    notify('Auftrag zugewiesen.', 'success');
  };

  if (board.loading) return <Loading />;

  const columns = (board.data?.columns ?? []).filter((column: any) =>
    ['new', 'triaged', 'assigned', 'accepted', 'in_progress', 'waiting_material', 'waiting_feedback'].includes(
      column.status,
    ),
  );
  const allTasks = columns.flatMap((column: any) => column.tasks);
  const unassigned = allTasks.filter((task: any) => !task.assignee_id);
  const criticalTasks = allTasks.filter((task: any) => task.criticality_level === 1);
  const inProgressTasks = allTasks.filter((task: any) => task.status === 'in_progress');
  const overdueTasks = allTasks.filter((task: any) => task.slaState === 'overdue');
  const criticalCount = criticalTasks.length;
  const inProgressCount = inProgressTasks.length;
  const overdueCount = overdueTasks.length;

  const LIST_FILTER_LABELS: Record<typeof listFilter, string> = {
    all: 'Alle',
    critical: 'Akute Notfälle',
    inprogress: 'In Bearbeitung',
    overdue: 'Überfällig',
  };
  const listTasks =
    listFilter === 'critical'
      ? criticalTasks
      : listFilter === 'inprogress'
        ? inProgressTasks
        : listFilter === 'overdue'
          ? overdueTasks
          : allTasks;
  const showList = (filter: typeof listFilter) => {
    setView('list');
    setListFilter(filter);
  };

  return (
    // Nur die Tafel füllt die Resthöhe – die übrigen Ansichten scrollen normal.
    // Beide Ansichten füllen die Resthöhe: die Tafel, damit ihre Spalten
    // nicht über das Fenster hinauswachsen, die Liste, damit ihre waagerechte
    // Scrollleiste am Fensterrand bleibt statt unter der letzten Zeile.
    <div className="stack fills">
      <ModuleHeader
        icon={<IconBoard width={22} height={22} />}
        title="Disposition"
        subtitle={
          <>
            Aufträge sichten, priorisieren und zuweisen. Statuswechsel per Drag &amp; Drop zwischen den Spalten.
          </>
        }
        pill="Steuerung"
        tone="info"
        actions={
          can('dispatch.route') && (
            <button className="btn accent" onClick={() => navigate('/routes')}>
              <IconRoute width={15} height={15} /> Tour planen
            </button>
          )
        }
      />

      <div className="grid cols-4">
        <StatCard
          label="Akute Notfälle"
          value={criticalCount}
          tone={criticalCount > 0 ? 'danger' : 'success'}
          emphasis={criticalCount > 0 ? 'alarm' : undefined}
          action={criticalCount > 0 ? 'Sofort handeln' : undefined}
          icon={<IconAlert width={17} height={17} />}
          hint={criticalCount > 0 ? 'Kritikalität der höchsten Stufe' : 'Kein Notfall offen'}
          onClick={criticalCount > 0 ? () => showList('critical') : undefined}
        />
        <StatCard
          label="In Bearbeitung"
          value={inProgressCount}
          tone="warning"
          icon={<IconFlash width={17} height={17} />}
          hint="Vor Ort begonnen"
          onClick={inProgressCount > 0 ? () => showList('inprogress') : undefined}
        />
        <StatCard
          label="Nicht zugewiesen"
          value={unassigned.length}
          tone={unassigned.length > 0 ? 'warning' : 'success'}
          icon={<IconUsers width={17} height={17} />}
          hint="Warten auf Disposition"
          onClick={() => setView('unassigned')}
        />
        <StatCard
          label="Überfällig"
          value={overdueCount}
          tone={overdueCount > 0 ? 'danger' : 'success'}
          emphasis={overdueCount > 0 ? 'attention' : undefined}
          action={overdueCount > 0 ? `${overdueCount} SLA verletzt` : undefined}
          icon={<IconClock width={17} height={17} />}
          hint={overdueCount > 0 ? 'Erledigungsfrist überschritten' : 'Alle Fristen eingehalten'}
          onClick={overdueCount > 0 ? () => showList('overdue') : undefined}
        />
      </div>

      <Tabs
        active={view}
        onChange={(key) => {
          setView(key as typeof view);
          // Ein direkter Klick auf den Reiter selbst meint "alle Aufträge" –
          // nur eine Kennzahlen-Kachel (siehe `showList`) soll vorfiltern.
          if (key === 'list') setListFilter('all');
        }}
        tabs={[
          { key: 'board', label: 'Kanban-Board', icon: <IconBoard width={15} height={15} /> },
          { key: 'list', label: 'Liste', count: allTasks.length, icon: <IconList width={15} height={15} /> },
          { key: 'unassigned', label: 'Zuweisung', count: unassigned.length },
          { key: 'calendar', label: 'Verfügbarkeit' },
        ]}
      />

      {/* Die Liste bekommt dieselbe Behandlung wie die Tafel: Sie füllt die
          Resthöhe, damit ihre waagerechte Scrollleiste am Fensterrand liegt
          und nicht erst nach dem Herunterscrollen aller Zeilen auftaucht. */}
      {view === 'list' && (
        <>
          {listFilter !== 'all' && (
            <div className="row between small subtle" style={{ padding: '0 2px' }}>
              <span>
                Gefiltert: <strong>{LIST_FILTER_LABELS[listFilter]}</strong> ({listTasks.length})
              </span>
              <button className="btn sm ghost" onClick={() => setListFilter('all')}>
                Zurücksetzen
              </button>
            </div>
          )}
          <TaskListView
            tasks={listTasks}
            users={users.data ?? []}
            canAssign={can('dispatch.assign')}
            storageKey="ff.dispatchList"
            onAssign={async (taskId, assigneeId) => {
              try {
                await api.post(`/api/tasks/${taskId}/assign`, { assigneeId });
                notify(assigneeId ? 'Auftrag zugewiesen.' : 'Zuweisung aufgehoben.', 'success');
                board.reload();
              } catch (error) {
                notify(error instanceof Error ? error.message : 'Zuweisung nicht möglich.', 'error');
              }
            }}
          />
        </>
      )}

      {view === 'board' && (
        <div className="kanban">
          {columns.map((column: any) => (
            <div
              key={column.status}
              className={`kanban-column ${dropTarget === column.status ? 'drop-target' : ''}`}
              onDragOver={(event) => {
                event.preventDefault();
                setDropTarget(column.status);
              }}
              onDragLeave={() => setDropTarget(null)}
              onDrop={() => {
                setDropTarget(null);
                if (dragged) void move(dragged, column.status);
                setDragged(null);
              }}
            >
              <div className="kanban-column-header">
                <span>{column.label}</span>
                <span className="count">{column.tasks.length}</span>
              </div>
              <div className="kanban-cards">
                {column.tasks.map((task: any) => (
                  <div
                    key={task.id}
                    className={`task-card ${task.criticality_level === 1 ? 'emergency' : ''}`}
                    style={{ borderLeftColor: task.criticality_color ?? 'var(--border-strong)' }}
                    draggable={can('tasks.status')}
                    onDragStart={() => setDragged(task)}
                    onClick={() => navigate(`/tasks/${task.id}`)}
                  >
                    <div className="row between" style={{ gap: 6 }}>
                      <span className="row" style={{ gap: 6, alignItems: 'center', minWidth: 0 }}>
                        {task.criticality_level === 1 ? (
                          <span className="emergency-flag">
                            <IconAlert width={10} height={10} /> Notfall
                          </span>
                        ) : (
                          <span className="task-no">{task.task_no}</span>
                        )}
                      </span>
                      <span className={`sla-dot sla-${task.slaState}`} title={SLA_LABELS[task.slaState]} />
                    </div>
                    {task.criticality_level === 1 && <span className="task-no">{task.task_no}</span>}
                    <div className="task-title">{task.title}</div>
                    <div className="task-meta">
                      {task.property_name && <span className="truncate">{task.property_name}</span>}
                      {task.due_at && <span>· {formatRelative(task.due_at)}</span>}
                    </div>
                    <div className="row between" style={{ marginTop: 8 }}>
                      <Badge color={task.criticality_color}>{task.criticality_name ?? '–'}</Badge>
                      {task.assignee_first_name ? (
                        <Avatar
                          firstName={task.assignee_first_name}
                          lastName={task.assignee_last_name}
                          size="sm"
                          title={`${task.assignee_first_name} ${task.assignee_last_name}`}
                        />
                      ) : (
                        <span className="small subtle">offen</span>
                      )}
                    </div>
                  </div>
                ))}
                {column.tasks.length === 0 && <div className="small subtle" style={{ padding: 8 }}>Keine Aufträge</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === 'unassigned' && (
        <Card title="Nicht zugewiesene Aufträge" subtitle="Direktzuweisung an verfügbare Mitarbeitende" noBody>
          {unassigned.length === 0 ? (
            <EmptyState title="Alles verteilt" hint="Aktuell warten keine Aufträge auf eine Zuweisung." />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Nummer</th>
                    <th>Titel</th>
                    <th>Objekt</th>
                    <th>Kritikalität</th>
                    <th>Fällig</th>
                    <th>Zuweisen an</th>
                  </tr>
                </thead>
                <tbody>
                  {unassigned.map((task: any) => (
                    <tr key={task.id}>
                      <td className="mono clickable" onClick={() => navigate(`/tasks/${task.id}`)}>
                        {task.task_no}
                      </td>
                      <td className="truncate" style={{ maxWidth: 300 }}>{task.title}</td>
                      <td className="small">{task.property_name ?? '–'}</td>
                      <td>
                        <Badge color={task.criticality_color}>{task.criticality_name}</Badge>
                      </td>
                      <td className="small">{task.due_at ? formatRelative(task.due_at) : '–'}</td>
                      <td>
                        <SearchSelect
                          value={''}
                          onChange={(wert) => wert && assign(task.id, Number(wert))}
                          options={(users.data ?? []).map((person: any) => ({ value: String(person.id), label: `${person.first_name} ${person.last_name}` }))}
                          disabled={!can('dispatch.assign')}
                          style={{ width: 200 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {view === 'calendar' && (
        <div className="grid cols-2">
          <Card title="Abwesenheiten & Vertretungen" subtitle="Grundlage für die Verfügbarkeitsprüfung" noBody>
            {(absences.data ?? []).length === 0 ? (
              <EmptyState title="Keine Abwesenheiten erfasst" />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Mitarbeiter</th>
                      <th>Art</th>
                      <th>Zeitraum</th>
                      <th>Vertretung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(absences.data ?? []).map((absence: any) => (
                      <tr key={absence.id}>
                        <td>
                          {absence.first_name} {absence.last_name}
                        </td>
                        <td>
                          <Badge variant="info">{absence.type}</Badge>
                        </td>
                        <td className="small">
                          {formatDate(absence.start_date)} – {formatDate(absence.end_date)}
                        </td>
                        <td className="small">
                          {absence.sub_first_name ? `${absence.sub_first_name} ${absence.sub_last_name}` : '–'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title="Auslastung" subtitle="Offene Aufträge je Mitarbeitendem" noBody>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Mitarbeiter</th>
                    <th>Rollen</th>
                    <th className="num">Offene Aufträge</th>
                  </tr>
                </thead>
                <tbody>
                  {(users.data ?? []).map((person: any) => {
                    const count = columns
                      .flatMap((column: any) => column.tasks)
                      .filter((task: any) => task.assignee_id === person.id).length;
                    return (
                      <tr key={person.id}>
                        <td>
                          <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
                            <Avatar firstName={person.first_name} lastName={person.last_name} size="sm" />
                            <span>
                              {person.first_name} {person.last_name}
                            </span>
                          </div>
                        </td>
                        <td className="small subtle">
                          {(person.roles ?? []).map((role: any) => role.name).join(', ') || '–'}
                        </td>
                        <td className="num">
                          <Badge variant={count > 4 ? 'warning' : count > 0 ? 'primary' : ''}>{count}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
